import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Layout } from "@/components/layout";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useBusinesses } from "@/hooks/use-businesses";
import { apiRequest } from "@/lib/queryClient";
import { BookOpen } from "lucide-react";
import { useLocation } from "wouter";

const fmt = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });

export default function GeneralLedgerPage() {
  const { activeBusiness } = useBusinesses();
  const businessId = activeBusiness?.id;
  const [location] = useLocation();

  // Parse account_id from URL if coming from Trial Balance drill-down
  const urlParams = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  const initialAccountId = urlParams.get("account_id") || "";

  const now = new Date();
  const [accountId, setAccountId] = useState(initialAccountId);
  const [startDate, setStartDate] = useState(`${now.getFullYear()}-01-01`);
  const [endDate, setEndDate] = useState(now.toISOString().split("T")[0]);

  // Fetch COA accounts for dropdown
  const { data: coaData } = useQuery<{ accounts: any[] }>({
    queryKey: ["/api/businesses", businessId, "chart-of-accounts"],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/businesses/${businessId}/chart-of-accounts`);
      return res.json();
    },
    enabled: !!businessId,
  });
  const accounts = coaData?.accounts || [];

  // Fetch GL data
  const { data: gl, isLoading } = useQuery({
    queryKey: ["/api/businesses", businessId, "reports/general-ledger", accountId, startDate, endDate],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/businesses/${businessId}/reports/general-ledger?account_id=${accountId}&start_date=${startDate}&end_date=${endDate}`);
      return res.json();
    },
    enabled: !!businessId && !!accountId && !!startDate && !!endDate,
  });

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <BookOpen className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">General Ledger</h1>
            <p className="text-muted-foreground text-sm">Transaction-level detail with running balance</p>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label>Account</Label>
                <Select value={accountId} onValueChange={setAccountId}>
                  <SelectTrigger><SelectValue placeholder="Select account..." /></SelectTrigger>
                  <SelectContent>
                    {accounts.map((a: any) => (
                      <SelectItem key={a.id} value={a.id.toString()}>{a.code} — {a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Start Date</Label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>End Date</Label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Data */}
        <Card>
          <CardContent className="p-0">
            {!accountId ? (
              <div className="p-12 text-center text-muted-foreground">
                <BookOpen className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p>Select an account to view its ledger.</p>
              </div>
            ) : isLoading ? (
              <div className="p-6 space-y-3">{[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-10" />)}</div>
            ) : !gl ? (
              <div className="p-12 text-center text-muted-foreground">No data found.</div>
            ) : (
              <>
                {/* Account header */}
                <div className="px-6 py-4 border-b bg-muted/30 flex items-center justify-between">
                  <div>
                    <span className="font-mono text-sm text-muted-foreground mr-2">{gl.account?.code}</span>
                    <span className="font-semibold">{gl.account?.name}</span>
                    <Badge variant="outline" className="ml-2 text-xs">{gl.account?.type}</Badge>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Opening: <span className="font-mono font-medium">{fmt(gl.openingBalance)}</span>
                  </div>
                </div>

                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="w-28">Date</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="w-28 text-right">Debit</TableHead>
                      <TableHead className="w-28 text-right">Credit</TableHead>
                      <TableHead className="w-32 text-right">Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {/* Opening balance row */}
                    <TableRow className="bg-blue-50/50">
                      <TableCell className="text-sm">{startDate}</TableCell>
                      <TableCell className="text-sm italic text-muted-foreground">Opening Balance</TableCell>
                      <TableCell className="text-right font-mono">{fmt(0)}</TableCell>
                      <TableCell className="text-right font-mono">{fmt(0)}</TableCell>
                      <TableCell className="text-right font-mono font-medium">{fmt(gl.openingBalance)}</TableCell>
                    </TableRow>

                    {gl.entries?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                          No approved transactions in this period.
                        </TableCell>
                      </TableRow>
                    ) : (
                      gl.entries?.map((e: any) => (
                        <TableRow key={e.id}>
                          <TableCell className="text-sm">{new Date(e.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</TableCell>
                          <TableCell className="text-sm">{e.description}</TableCell>
                          <TableCell className="text-right font-mono">{fmt(e.debit)}</TableCell>
                          <TableCell className="text-right font-mono">{fmt(e.credit)}</TableCell>
                          <TableCell className="text-right font-mono font-medium">{fmt(e.balance)}</TableCell>
                        </TableRow>
                      ))
                    )}

                    {/* Closing balance row */}
                    <TableRow className="bg-muted/50 font-bold border-t-2">
                      <TableCell colSpan={4} className="text-right">Closing Balance</TableCell>
                      <TableCell className="text-right font-mono">{fmt(gl.closingBalance)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
